import Hide from '@kiwicom/orbit-components/lib/Hide';
import useMediaQuery from '@kiwicom/orbit-components/lib/hooks/useMediaQuery';
import { Devices } from '@kiwicom/orbit-components/lib/utils/mediaQuery';
import React, { ReactNode } from 'react';

/**
 * Used to select a range of devices, given a lower and upper bound
 * on screen size. Ordered from smallest to largest so that increasing
 * index corresponds to increasing screen size.
 */
// eslint-disable-next-line import/no-named-export
export const DEVICES_IN_ORDER = [
  'smallMobile',
  'mediumMobile',
  'largeMobile',
  'tablet',
  'desktop',
  'largeDesktop',
] as const;

type Assert<T extends true> = T;

type DevicesInTheArray = (typeof DEVICES_IN_ORDER)[number];
type EnsureAllDevicesAreInTheArray = Devices extends DevicesInTheArray ? true : false;

// This throws a type error if a device is not in the array (e.g. if
// one day Orbit invents a new device type).
type AssertAllDevicesAreInTheArray = Assert<EnsureAllDevicesAreInTheArray>;

/**
 * @returns the current device, or null during server-side rendering
 * (because the server does not have a screen).
 */
const useDevice = (): Devices | null => {
  const { isMediumMobile, isLargeMobile, isTablet, isDesktop, isLargeDesktop } = useMediaQuery();

  // During server-side rendering, the media query hook returns null.
  if (isMediumMobile === null) return null;

  // Queries for smaller devices return true for large devices too, so
  // we need to check for the largest devices first.
  const media = [
    { flag: isLargeDesktop, device: 'largeDesktop' as const },
    { flag: isDesktop, device: 'desktop' as const },
    { flag: isTablet, device: 'tablet' as const },
    { flag: isLargeMobile, device: 'largeMobile' as const },
    { flag: isMediumMobile, device: 'mediumMobile' as const },
    { flag: true, device: 'smallMobile' as const },
  ];

  for (const { flag, device } of media) {
    if (flag) return device;
  }

  return 'smallMobile';
};

// eslint-disable-next-line import/no-named-export
export type MediaProps = {
  greaterThanOrEqual?: Devices;
  lessThan?: Devices;
  children: ReactNode;
};

/**
 * Displays children iff the device screen size is within `greaterThanOrEqual`
 * and `lessThan`.
 *
 * If either is not provided, the corresponding condition is assumed to be
 * satisfied. In particular, if neither are provided, the children are always
 * displayed. If both are provided, both conditions must be satisfied for
 * the children to be displayed.
 *
 * During server-side rendering, the children are always rendered. They will be
 * hidden using CSS media queries on the client if necessary.
 *
 * @returns The children wrapped in Orbit's Hide component, or (on the client)
 * null if the corresponding media query is not satisfied.
 */
const Media = ({ greaterThanOrEqual, lessThan, children }: MediaProps) => {
  const device = useDevice();

  const ltIndex = lessThan ? DEVICES_IN_ORDER.indexOf(lessThan) : DEVICES_IN_ORDER.length;
  const gteIndex = greaterThanOrEqual ? DEVICES_IN_ORDER.indexOf(greaterThanOrEqual) : 0;

  const devicesToHide = DEVICES_IN_ORDER.filter((_, index) => index < gteIndex || index >= ltIndex);

  // On the server, we always display the children.
  const deviceIndex = device ? DEVICES_IN_ORDER.indexOf(device) : null;
  const isDeviceInRange =
    deviceIndex === null || (deviceIndex >= gteIndex && deviceIndex < ltIndex);

  return isDeviceInRange ? (
    <Hide block on={devicesToHide}>
      {children}
    </Hide>
  ) : null;
};

export default Media;
